import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { submitProductReview } from "@/lib/reviews.functions";

type Review = {
  id: string;
  reviewer_name: string;
  rating: number;
  title: string;
  body: string;
  is_verified_buyer: boolean;
  created_at: string;
};

function Stars({ value, onChange }: { value: number; onChange?: (n: number) => void }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange?.(n)}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          disabled={!onChange}
          className={onChange ? "cursor-pointer" : "cursor-default"}
        >
          <Star className={`h-5 w-5 ${n <= value ? "fill-gold text-gold" : "text-muted-foreground"}`} />
        </button>
      ))}
    </div>
  );
}

export function ProductReviews({ productSlug }: { productSlug: string }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // form state
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submitReview = useServerFn(submitProductReview);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("product_reviews")
      .select("id, reviewer_name, rating, title, body, is_verified_buyer, created_at")
      .eq("product_slug", productSlug)
      .order("created_at", { ascending: false })
      .limit(50);
    if (!error) setReviews((data ?? []) as Review[]);
    setLoading(false);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSlug]);

  const avg =
    reviews.length > 0
      ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length
      : 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast.message("Please sign in to leave a review.");
      return;
    }
    if (body.trim().length < 10) {
      toast.error("Review must be at least 10 characters.");
      return;
    }
    if (name.trim().length < 2) {
      toast.error("Please enter your name.");
      return;
    }
    setSubmitting(true);
    let result: { status: "published" | "pending_approval"; id: string } | null = null;
    try {
      result = await submitReview({
        data: {
          product_slug: productSlug,
          rating,
          title,
          body,
          reviewer_name: name,
        },
      });
    } catch (err) {
      setSubmitting(false);
      toast.error(err instanceof Error ? err.message : "Failed to submit review");
      return;
    }
    setSubmitting(false);
    if (result?.status === "pending_approval") {
      toast.success("Thank you — your review is awaiting approval.");
    } else {
      toast.success("Thank you for your review.");
    }
    setTitle("");
    setBody("");
    setShowForm(false);
    void load();
  };

  return (
    <section className="container-luxe py-20 border-t border-border">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
        <div>
          <div className="eyebrow mb-3">From Our Owners</div>
          <h2 className="font-serif text-4xl md:text-5xl">Reviews</h2>
          {reviews.length > 0 && (
            <div className="mt-4 flex items-center gap-3">
              <Stars value={Math.round(avg)} />
              <span className="text-sm text-muted-foreground tabular-nums">
                {avg.toFixed(1)} · {reviews.length} review{reviews.length === 1 ? "" : "s"}
              </span>
            </div>
          )}
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="text-[11px] uppercase tracking-[0.28em] border border-foreground px-6 py-3 hover:bg-foreground hover:text-background transition-colors"
        >
          {showForm ? "Cancel" : "Write a Review"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mb-12 bg-secondary p-8 max-w-2xl">
          {!user && (
            <p className="text-sm mb-4">
              Please <Link to="/auth" className="underline">sign in</Link> to submit a review.
            </p>
          )}
          <div className="mb-4">
            <label className="eyebrow block mb-2">Rating</label>
            <Stars value={rating} onChange={setRating} />
          </div>
          <div className="mb-4">
            <label className="eyebrow block mb-2" htmlFor="r-name">Name</label>
            <input
              id="r-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              required
              className="w-full bg-background border border-border px-3 py-2 text-sm"
            />
          </div>
          <div className="mb-4">
            <label className="eyebrow block mb-2" htmlFor="r-title">Title (optional)</label>
            <input
              id="r-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={300}
              className="w-full bg-background border border-border px-3 py-2 text-sm"
            />
          </div>
          <div className="mb-4">
            <label className="eyebrow block mb-2" htmlFor="r-body">Your review</label>
            <textarea
              id="r-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              maxLength={4000}
              required
              className="w-full bg-background border border-border px-3 py-2 text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !user}
            className="bg-foreground text-background px-8 py-3 text-[11px] uppercase tracking-[0.28em] disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit Review"}
          </button>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading reviews…</p>
      ) : reviews.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No reviews yet. Be the first to share your experience.
        </p>
      ) : (
        <div className="grid md:grid-cols-2 gap-8">
          {reviews.map((r) => (
            <article key={r.id} className="border border-border p-6">
              <div className="flex items-center justify-between mb-3">
                <Stars value={r.rating} />
                {r.is_verified_buyer && (
                  <span className="text-[10px] uppercase tracking-[0.24em] text-gold">
                    Verified Buyer
                  </span>
                )}
              </div>
              {r.title && <h3 className="font-serif text-xl mb-2">{r.title}</h3>}
              <p className="text-sm leading-relaxed text-foreground/80">{r.body}</p>
              <div className="mt-4 text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
                {r.reviewer_name} · {new Date(r.created_at).toLocaleDateString()}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
